// settings/pages/OrganizationPage.tsx — COMPLETE PREMIUM DASHBOARD
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuthStore } from '@/store/authStore';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Spinner } from '@/components/ui/Spinner';
import * as api from '../services/settingsApi';
import {
 Store, ArrowLeft, Building2, Users, Package, Receipt,
 TrendingUp, Clock, MapPin, Phone, Mail, Globe, Hash,
 Settings, Pencil, CheckCircle2, Activity, CreditCard,
 BarChart3, Monitor, Calendar, AlertCircle, ChevronRight,
 Banknote, Sparkles, ShieldCheck, Cog, Warehouse, ClipboardList
} from 'lucide-react';
import { motion } from 'framer-motion';

// ============================================================
// FORMAT HELPERS
// ============================================================
const formatNaira = (val: number) =>
 new Intl.NumberFormat('en-NG', { style: 'currency', currency: 'NGN', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(val);

const formatDate = (dateString: string) =>
 new Date(dateString).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });

const timeAgo = (dateString: string) => {
 const diff = Date.now() - new Date(dateString).getTime();
 const minutes = Math.floor(diff / 60000);
 if (minutes < 60) return `${minutes}m ago`;
 const hours = Math.floor(minutes / 60);
 if (hours < 24) return `${hours}h ago`;
 return `${Math.floor(hours / 24)}d ago`;
};

// ============================================================
// RING STAT CARD
// ============================================================
const RingStatCard = ({ label, value, subValue, icon: Icon, gradient, progress = 100, onClick }: {
 label: string; value: string | number; subValue?: string; icon: React.ElementType;
 gradient: string; progress?: number; onClick?: () => void;
}) => {
 const radius = 28;
 const circumference = 2 * Math.PI * radius;
 const offset = circumference - (progress / 100) * circumference;
 const id = `gradient-${label.replace(/\s+/g, '')}`;

 return (
 <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
 whileHover={{ y: -3 }} onClick={onClick}
 className={`relative bg-white rounded-2xl border border-slate-200 shadow-sm p-4 hover:shadow-lg transition-all duration-300 group ${onClick ? 'cursor-pointer' : ''}`}>
 <div className="flex items-center gap-3">
 <div className="relative w-16 h-16 flex-shrink-0">
 <svg className="w-16 h-16 -rotate-90" viewBox="0 0 64 64">
 <circle cx="32" cy="32" r={radius} fill="none" stroke="currentColor"
 className="text-slate-100 " strokeWidth="5" />
 <motion.circle cx="32" cy="32" r={radius} fill="none"
 stroke={`url(#${id})`}
 strokeWidth="5" strokeLinecap="round"
 initial={{ strokeDashoffset: circumference }}
 animate={{ strokeDashoffset: offset }}
 transition={{ duration: 1.2, ease: "easeOut" }}
 strokeDasharray={circumference}
 />
 <defs>
 <linearGradient id={id} x1="0%" y1="0%" x2="100%" y2="0%">
 <stop offset="0%" stopColor="#10b981" />
 <stop offset="100%" stopColor="#14b8a6" />
 </linearGradient>
 </defs>
 </svg>
 <div className="absolute inset-0 flex items-center justify-center">
 <div className={`p-1.5 rounded-full bg-gradient-to-br ${gradient} shadow-md`}>
 <Icon className="w-4 h-4 text-white" />
 </div>
 </div>
 </div>
 <div className="flex-1 min-w-0">
 <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">{label}</p>
 <p className="text-xl font-bold text-slate-900 mt-0.5">{value}</p>
 {subValue && <p className="text-xs text-slate-400 mt-0.5">{subValue}</p>}
 </div>
 {onClick && <ChevronRight className="w-4 h-4 text-slate-300 group-hover:text-emerald-500 group-hover:translate-x-1 transition-all flex-shrink-0" />}
 </div>
 </motion.div>
 );
};

// ============================================================
// QUICK LINK CARD
// ============================================================
const QuickLinkCard = ({ label, icon: Icon, gradient, borderColor, textColor, onClick }: {
 label: string; icon: React.ElementType; gradient: string; borderColor: string; textColor: string; onClick: () => void;
}) => (
 <motion.button whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.98 }} onClick={onClick}
 className={`relative bg-gradient-to-br ${gradient} border ${borderColor} rounded-2xl p-4 text-left hover:shadow-lg transition-all group`}>
 <Icon className="w-6 h-6 mb-3 opacity-80 group-hover:opacity-100 transition" />
 <p className={`text-sm font-semibold ${textColor}`}>{label}</p>
 </motion.button>
);

// ============================================================
// STATUS INDICATOR
// ============================================================
const StatusIndicator = ({ label, value, ok }: { label: string; value: string; ok: boolean }) => (
 <div className="flex items-center justify-between py-2.5">
 <span className="text-sm text-slate-500">{label}</span>
 <div className="flex items-center gap-2">
 <span className="text-sm font-semibold text-slate-700 ">{value}</span>
 {ok ? <CheckCircle2 size={16} className="text-emerald-500" /> : <AlertCircle size={16} className="text-amber-500" />}
 </div>
 </div>
);

// ============================================================
// CONFIG INFO LINE
// ============================================================
const ConfigLine = ({ label, value, icon: Icon }: { label: string; value: string | undefined; icon: React.ElementType }) => (
 <div className="flex items-center gap-3 py-2.5">
 <div className="p-1.5 rounded-lg bg-slate-100 flex-shrink-0">
 <Icon size={14} className="text-slate-500" />
 </div>
 <div className="flex-1 min-w-0">
 <p className="text-xs text-slate-400">{label}</p>
 <p className="text-sm font-medium text-slate-700 truncate">{value || 'Not set'}</p>
 </div>
 </div>
);

// ============================================================
// MAIN COMPONENT
// ============================================================

export function OrganizationPage() {
 const navigate = useNavigate();
 const user = useAuthStore((s) => s.user);
 const [greetingTime] = useState(() => {
 const hour = new Date().getHours();
 if (hour < 12) return 'morning';
 if (hour < 17) return 'afternoon';
 return 'evening';
 });

 const { data: tenant, isLoading } = useQuery({
 queryKey: ['tenant', user?.tenantId],
 queryFn: () => api.getTenant(user!.tenantId),
 enabled: !!user?.tenantId,
 });

 const { data: branches } = useQuery({
 queryKey: ['branches', user?.tenantId],
 queryFn: () => api.getBranches(user!.tenantId),
 enabled: !!user?.tenantId,
 });

 const { data: staffData } = useQuery({
 queryKey: ['staff', user?.tenantId],
 queryFn: () => api.getStaff(user!.tenantId),
 enabled: !!user?.tenantId,
 });

 const { data: taxes } = useQuery({
 queryKey: ['taxes', user?.tenantId],
 queryFn: () => api.getTaxes(user!.tenantId),
 enabled: !!user?.tenantId,
 });

 const { data: terminals } = useQuery({
 queryKey: ['pos-terminals', user?.tenantId],
 queryFn: () => api.getPosTerminals(user!.tenantId),
 enabled: !!user?.tenantId,
 });

 const { data: bankAccounts } = useQuery({
 queryKey: ['bank-accounts', user?.tenantId],
 queryFn: () => api.getBankAccounts(user!.tenantId),
 enabled: !!user?.tenantId,
 });

 if (isLoading) {
 return (
 <div className="flex items-center justify-center min-h-[80vh]">
 <div className="text-center">
 <motion.div animate={{ rotate: 360 }} transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
 className="w-16 h-16 rounded-2xl border-4 border-emerald-200 border-t-emerald-600 mx-auto mb-6" />
 <p className="text-slate-500 font-medium">Loading business overview...</p>
 </div>
 </div>
 );
 }

 const settings = tenant?.settings || {};
 const activeBranches = (branches || []).filter((b: any) => b.isActive).length;
 const activeStaff = (staffData?.data || []).filter((s: any) => s.isActive).length;
 const activeTaxes = (taxes || []).filter((t: any) => t.isActive).length;
 const onlineTerminals = (terminals || []).filter((t: any) => t.lastSeenAt && new Date(t.lastSeenAt).getTime() > Date.now() - 10 * 60 * 1000).length;
 const totalBankBalance = (bankAccounts || []).reduce((sum: number, a: any) => sum + parseFloat(a.currentBalance || '0'), 0);
 const activeTerminals = (terminals || []).filter((t: any) => t.isActive).length;

 const greeting = greetingTime === 'morning' ? 'Good Morning' : greetingTime === 'afternoon' ? 'Good Afternoon' : 'Good Evening';

 return (
 <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-emerald-50/30">
 <div className="max-w-[1600px] mx-auto p-4 lg:p-6 space-y-5">

 {/* ========== GRADIENT HERO HEADER ========== */}
 <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }}
 className="relative overflow-hidden bg-gradient-to-br from-emerald-600 via-emerald-700 to-teal-700 rounded-3xl shadow-2xl">
 <div className="absolute inset-0 opacity-20">
 <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(255,255,255,0.3),transparent_50%)]" />
 <div className="absolute top-0 right-0 w-96 h-96 bg-white/10 rounded-full blur-3xl" />
 <div className="absolute bottom-0 left-0 w-64 h-64 bg-teal-400/20 rounded-full blur-3xl" />
 </div>

 <div className="relative px-6 lg:px-8 py-6 lg:py-8">
 <div className="flex items-center justify-between mb-6">
 <button onClick={() => navigate('/settings')}
 className="flex items-center gap-2 text-emerald-100 hover:text-white transition-colors text-sm font-medium">
 <ArrowLeft className="w-4 h-4" />
 Back to Settings
 </button>
 <div className="flex items-center gap-2">
 <Button
 size="sm"
 variant="secondary"
 leftIcon={<Pencil size={14} />}
 onClick={() => navigate('/settings/organization/profile')}
 className="bg-white/10 backdrop-blur-sm text-white border-white/20 hover:bg-white/20"
 >
 Edit Profile
 </Button>
 </div>
 </div>

 <div className="flex flex-col lg:flex-row items-start lg:items-center gap-6">
 <motion.div whileHover={{ scale: 1.02 }} className="relative flex-shrink-0">
 <div className="w-20 h-20 lg:w-24 lg:h-24 bg-gradient-to-br from-white/30 to-white/10 rounded-2xl flex items-center justify-center shadow-2xl ring-4 ring-white/20 backdrop-blur-sm">
 <Store className="w-10 h-10 lg:w-12 lg:h-12 text-white" />
 </div>
 <div className="absolute -bottom-1.5 -right-1.5 w-6 h-6 bg-emerald-400 rounded-full ring-2 ring-white flex items-center justify-center">
 <Sparkles className="w-3.5 h-3.5 text-white" />
 </div>
 </motion.div>

 <div className="flex-1 min-w-0">
 <h1 className="text-2xl lg:text-3xl font-bold text-white tracking-tight">
 {greeting}, {user?.firstName || 'Admin'} 👋
 </h1>
 <div className="flex flex-wrap items-center gap-3 mt-2">
 <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white/10 backdrop-blur-sm rounded-xl text-emerald-50 text-sm">
 <Store className="w-3.5 h-3.5" /> {tenant?.name || 'Your Business'}
 </span>
 {tenant?.email && (
 <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white/10 backdrop-blur-sm rounded-xl text-emerald-50 text-sm">
 <Mail className="w-3.5 h-3.5" /> {tenant.email}
 </span>
 )}
 <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white/10 backdrop-blur-sm rounded-xl text-emerald-50 text-sm">
 <Badge variant="success" size="sm">Active</Badge>
 </span>
 </div>
 </div>

 <div className="flex gap-4 flex-shrink-0">
 <div className="text-center px-5 py-3 bg-white/10 backdrop-blur-sm rounded-2xl">
 <p className="text-2xl font-bold text-white">{branches?.length || 0}</p>
 <p className="text-xs text-emerald-200">Branches</p>
 </div>
 <div className="text-center px-5 py-3 bg-white/10 backdrop-blur-sm rounded-2xl">
 <p className="text-2xl font-bold text-white">{staffData?.data?.length || 0}</p>
 <p className="text-xs text-emerald-200">Staff</p>
 </div>
 <div className="text-center px-5 py-3 bg-white/10 backdrop-blur-sm rounded-2xl">
 <p className="text-2xl font-bold text-white">{formatNaira(totalBankBalance)}</p>
 <p className="text-xs text-emerald-200">Balance</p>
 </div>
 </div>
 </div>
 </div>
 </motion.div>

 {/* ========== RING STATS ROW ========== */}
 <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
 <RingStatCard label="Branch Health" value={`${activeBranches}/${branches?.length || 0}`}
 subValue="active locations" icon={Building2} gradient="from-blue-500 to-indigo-600"
 progress={branches?.length ? (activeBranches / branches.length) * 100 : 0}
 onClick={() => navigate('/settings/branches')} />
 <RingStatCard label="Staff Active" value={`${activeStaff}/${staffData?.data?.length || 0}`}
 subValue="team members" icon={Users} gradient="from-purple-500 to-violet-600"
 progress={staffData?.data?.length ? (activeStaff / staffData.data.length) * 100 : 0}
 onClick={() => navigate('/settings/staff')} />
 <RingStatCard label="Terminals Online" value={`${onlineTerminals}/${activeTerminals || 0}`}
 subValue="POS devices" icon={Monitor} gradient="from-cyan-500 to-sky-600"
 progress={activeTerminals ? (onlineTerminals / activeTerminals) * 100 : 0}
 onClick={() => navigate('/settings/terminals')} />
 <RingStatCard label="Taxes Active" value={`${activeTaxes}/${taxes?.length || 0}`}
 subValue="tax rates" icon={Receipt} gradient="from-teal-500 to-cyan-600"
 progress={taxes?.length ? (activeTaxes / taxes.length) * 100 : 0}
 onClick={() => navigate('/settings/taxes')} />
 </div>

 {/* ========== TWO COLUMN LAYOUT ========== */}
 <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">

 {/* LEFT: Configuration Summary + Quick Links */}
 <div className="lg:col-span-2 space-y-5">
 <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
 <div className="px-6 py-4 border-b border-slate-100 bg-gradient-to-r from-emerald-50/30 to-white flex items-center justify-between">
 <div className="flex items-center gap-3">
 <div className="p-2 rounded-xl bg-gradient-to-br from-emerald-50 to-teal-50 ring-1 ring-emerald-200">
 <Cog className="w-5 h-5 text-emerald-600" />
 </div>
 <div>
 <h3 className="font-semibold text-slate-800">Business Configuration</h3>
 <p className="text-xs text-slate-500">Current settings overview</p>
 </div>
 </div>
 <Button variant="ghost" size="xs" leftIcon={<Pencil size={12} />} onClick={() => navigate('/settings/organization/profile')}>
 Edit
 </Button>
 </div>
 <div className="p-5 grid grid-cols-2 gap-1">
 <ConfigLine label="Company Name" value={tenant?.name} icon={Store} />
 <ConfigLine label="Tax ID" value={tenant?.taxId} icon={Hash} />
 <ConfigLine label="Currency" value={settings.currency || 'NGN'} icon={Globe} />
 <ConfigLine label="Timezone" value={settings.timezone || 'Africa/Lagos'} icon={Clock} />
 <ConfigLine label="Date Format" value={settings.dateFormat || 'DD/MM/YYYY'} icon={Calendar} />
 <ConfigLine label="Tax Rate" value={`${settings.taxRate || '7.5'}% (${settings.taxLabel || 'VAT'})`} icon={Receipt} />
 </div>
 </div>

 {/* Quick Link Cards */}
 <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
 <QuickLinkCard label="Branches" icon={Building2}
 gradient="from-blue-500/20 to-indigo-500/20" borderColor="border-blue-400/30" textColor="text-blue-300"
 onClick={() => navigate('/settings/branches')} />
 <QuickLinkCard label="Staff & Roles" icon={Users}
 gradient="from-purple-500/20 to-violet-500/20" borderColor="border-purple-400/30" textColor="text-purple-300"
 onClick={() => navigate('/settings/staff')} />
 <QuickLinkCard label="Inventory" icon={Warehouse}
 gradient="from-amber-500/20 to-orange-500/20" borderColor="border-amber-400/30" textColor="text-amber-300"
 onClick={() => navigate('/settings/organization/inventory')} />
 <QuickLinkCard label="Procurement" icon={ClipboardList}
 gradient="from-rose-500/20 to-pink-500/20" borderColor="border-rose-400/30" textColor="text-rose-300"
 onClick={() => navigate('/settings/organization/procurement')} />
 </div>
 </div>

 {/* RIGHT: Business Status + Activity */}
 <div className="space-y-5">
 <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
 <div className="px-5 py-4 border-b border-slate-100 ">
 <h3 className="font-semibold text-slate-900 text-sm">Business Health</h3>
 </div>
 <div className="p-5 space-y-1">
 <StatusIndicator label="Branches Active" value={`${activeBranches}/${branches?.length || 0}`} ok={activeBranches > 0} />
 <StatusIndicator label="Staff Active" value={`${activeStaff}/${staffData?.data?.length || 0}`} ok={activeStaff > 0} />
 <StatusIndicator label="Terminals Online" value={`${onlineTerminals}/${activeTerminals || 0}`} ok={onlineTerminals > 0} />
 <StatusIndicator label="Tax Rates" value={`${activeTaxes} configured`} ok={activeTaxes > 0} />
 <StatusIndicator label="Bank Accounts" value={`${bankAccounts?.length || 0} linked`} ok={(bankAccounts?.length || 0) > 0} />
 </div>
 </div>

 <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
 <div className="px-5 py-4 border-b border-slate-100 ">
 <h3 className="font-semibold text-slate-900 text-sm">Recent Activity</h3>
 </div>
 <div className="p-5 divide-y divide-slate-50 ">
 {tenant?.createdAt && (
 <div className="flex gap-3 py-3">
 <div className="w-9 h-9 rounded-xl bg-emerald-50 flex items-center justify-center flex-shrink-0">
 <Store size={16} className="text-emerald-600" />
 </div>
 <div className="flex-1 min-w-0">
 <p className="text-sm font-medium text-slate-700 ">Business Created</p>
 <p className="text-xs text-slate-400">{timeAgo(tenant.createdAt)}</p>
 </div>
 </div>
 )}
 {branches?.length > 0 && (
 <div className="flex gap-3 py-3">
 <div className="w-9 h-9 rounded-xl bg-blue-50 flex items-center justify-center flex-shrink-0">
 <Building2 size={16} className="text-blue-600" />
 </div>
 <div className="flex-1 min-w-0">
 <p className="text-sm font-medium text-slate-700 ">{branches.length} Branch{branches.length > 1 ? 'es' : ''} Configured</p>
 <p className="text-xs text-slate-400">{activeBranches} active</p>
 </div>
 </div>
 )}
 </div>
 </div>
 </div>
 </div>
 </div>
 </div>
 );
}